// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import {
  State,
  selectRoleDetail,
  LoadingRoleDetailsAction,
  selectRoles,
  LoadingRolesAction,
} from '@app/access-management/state';
import { Observable, Subscription } from 'rxjs';
import { take, filter, map, switchMap } from 'rxjs/operators';
import {
  readParameterFromRouterState,
  RoleDetail,
  MaybeLoadable,
  Loaded,
  needsLoading,
  Loadable,
  GoAction,
  IAM_ROUTE,
  QueueAlertAction,
  AlertType,
  RolesService,
  Role
} from '@app/core';

const shouldLoadRole = (roleDetail: RoleDetail | undefined) =>
  roleDetail === undefined || roleDetail.role === undefined;

const shouldLoadRoles = (roles: MaybeLoadable) => needsLoading<Loaded<Role[]>>(roles);
const hasRolesState = (roles: MaybeLoadable) => !needsLoading<Loaded<Role[]>>(roles);

@Component({
  selector: 'app-group-roles-form',
  templateUrl: './group-roles-form.component.html',
  styleUrls: ['./group-roles-form.component.scss']
})
export class GroupRolesFormComponent implements OnInit, OnDestroy {
  readonly role$: Observable<Role | undefined>;
  readonly roleDetail$: Observable<RoleDetail>;
  readonly loadableRoles$: Observable<Loadable>;
  readonly roles$: Observable<Role[] | undefined>;
  private roleId: string;
  private submitSubscription: Subscription | undefined = undefined;
  selectedRoles: Role[] | undefined = undefined;

  constructor(activatedRoute: ActivatedRoute, private store: Store<State>, private roleService: RolesService) {
    const roleId = readParameterFromRouterState(activatedRoute, 'id');
    if (!roleId) {
      throw new Error('role id not in route');
    }
    this.roleId = roleId;
    const maybeRoleDetail$: Observable<RoleDetail | undefined> = this.store.pipe(select(selectRoleDetail(this.roleId)));

    maybeRoleDetail$
      .pipe(take(1), filter((roleDetail: RoleDetail | undefined) => shouldLoadRole(roleDetail)))
      .subscribe(() => store.dispatch(new LoadingRoleDetailsAction(this.roleId)));

    this.roleDetail$ = maybeRoleDetail$.pipe(filter<RoleDetail>(roleDetail => roleDetail !== undefined));

    this.role$ = this.roleDetail$.pipe(map(roleDetail => roleDetail.role));

    this.loadableRoles$ = this.store.pipe(select(selectRoles));
    this.loadableRoles$
      .pipe(take(1), filter(shouldLoadRoles))
      .subscribe(() => store.dispatch(new LoadingRolesAction()));

    this.roles$ = this.loadableRoles$.pipe(
      filter<Loaded<Role[]> | Loadable>(hasRolesState),
      map(roleList => roleList.data)
    );
  }

  ngOnInit() {}

  ngOnDestroy() {
    if (this.submitSubscription !== undefined) {
      this.submitSubscription.unsubscribe();
    }
  }

  onSelectRole(selectedRoles: Role[]) {
    this.selectedRoles = selectedRoles;
  }

  submit() {

    this.goToRoleDetailsPage();
    this.store.dispatch(
        new QueueAlertAction({
          alertText: `Roles have successfully been attached to this role.`,
          alertType: AlertType.SUCCESS
        })
    );


    // Todo: call [Update Role] service to patch new roles' ids
    // might be better to delegate to a separate endpoint like v1/role/role ?
    // const submit$ = this.roleDetail$.pipe(
    //   map(roleDetail => this.getUpdateRoleRoleRequest(roleDetail, this.selectedRoles)),
    //   switchMap(request => this.roleService.updateRoleDetail(this.roleId, request))
    // );

    // this.submitSubscription = submit$.subscribe(
    //   _ => {
    //     this.goToRoleDetailsPage();
    //     this.store.dispatch(new LoadingRoleDetailsAction(this.roleId));
    //     this.store.dispatch(
    //       new QueueAlertAction({
    //         alertText: `Roles have successfully been attached to this role.`,
    //         alertType: AlertType.SUCCESS
    //       })
    //     );
    //   },
    //   err => {
    //     this.goToRoleDetailsPage();
    //     this.store.dispatch(
    //       new QueueAlertAction({
    //         alertText: `Failed to attached roles to this role.`,
    //         alertType: AlertType.ERROR
    //       })
    //     );
    //   }
    // );
  }

  cancel() {
    // Todo: probably need to clear role selection state
    this.goToRoleDetailsPage();
  }

//   private getUpdateRoleRoleRequest(roleDetail: RoleDetail, newRoles: Role[] | undefined) {
//     const roleIds = roleDetail.roles !== undefined ? roleDetail.roles.map(role => role.id) : [];
//     const newRoleIds = newRoles !== undefined ? newRoles.map(role => role.id) : [];
//     return {
//       roleIds: Array.from(new Set([...roleIds, ...newRoleIds])) // remove dups in case
//     };
//   }

  private goToRoleDetailsPage() {
    this.store.dispatch(new GoAction(['/', IAM_ROUTE, 'roles', this.roleId]));
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/groups/group-details/group-roles-form/group-roles-form.component.ts